/**
 * TAZMİNAT HESAP MOTORU (dönem tablolu)
 * =====================================
 * İş gücü kaybı (sürekli/geçici iş göremezlik) ve destekten yoksun kalma
 * hesabı. Bilirkişi raporlarındaki gibi zaman DÖNEMLERE bölünür ve her
 * dönemde gelir, kayıp ve (destekte) paylar ayrı hesaplanır.
 *
 * DÖNEMLER
 *   Bilinen (geçmiş) dönem : olay tarihi → hesap (rapor) tarihi. Gelir her
 *                            asgari ücret döneminde o dönemin tutarıdır;
 *                            artış/iskonto yoktur.
 *   Bilinmeyen (gelecek)   : hesap tarihinden sonra takvim yılı dilimleri.
 *                            Gelir hesap tarihindeki gelirden başlar ve
 *                            progresif rantla (artış g, iskonto i) yıl yıl
 *                            yürür: k'ıncı yıl çarpanı ((1+g)/(1+i))^k.
 *                            g = i ise gelir sabit kalır (Yargıtay %10/%10).
 *   Aktif dönem            : çalışma yaşları (başlangıç yaşı → bitiş yaşı).
 *   Pasif dönem            : aktif dönem sonu → bakiye ömür sonu; gelir
 *                            asgari ücret (ya da girilen pasif gelir).
 *
 * GELİR
 *   Ücret girilmediyse her dönemde net asgari ücret. Ücret girildiyse o
 *   ücret, asgari ücretin artış oranında güncellenir:
 *     gelir(t) = ücret × asgari(t) / asgari(olay)   (asgari ücretin altına inmez)
 *   18 yaşından (aktif başlangıç yaşından) önce gelir kaybı yoktur.
 *
 * AY SAYMA
 *   Tam aylar 1, kalan günler gün/30 sayılır. Tam bir takvim yılı = 12 ay
 *   (365/30 = 12,17 sayılmaz).
 *
 * DESTEK PAYLARI (Yargıtay / AYİM)
 *   Müteveffa 2, eş 2, her çocuk 1, anne ve baba 1'er pay. Bir çocuğun
 *   desteği bittiğinde (18 / 22 / 25 yaş) ya da bir hak sahibinin ömrü
 *   dolduğunda paylar o tarihten itibaren YENİDEN dağıtılır.
 *   Eşin yeniden evlenme ihtimali (AYİM) eşin gelecek dönem payından düşülür.
 *
 * Bütün tutarlar kuruşa yuvarlanır. Bu dosya ekran içermez; test edilebilir.
 */

import { getBakiyeOmur } from './trh2010';
import { getAsgariNet, getAsgariNetAgiHaric, getAsgariBrut } from './asgariUcret';

const MS_GUN = 24 * 60 * 60 * 1000;
const YIL_GUN = 365.25;

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const sayi = (v, varsayilan = 0) => {
  if (v === '' || v == null) return varsayilan;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : varsayilan;
};

/* ---------------- Tarih yardımcıları (yerel gün; saat dilimi kayması yok) ---------------- */

export function gunOku(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : new Date(v.getFullYear(), v.getMonth(), v.getDate());
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
export const gunEkle = (d, n) => { const x = new Date(d.getTime()); x.setDate(x.getDate() + n); return x; };
const yilEkleTam = (d, y) => {
  const x = new Date(d.getFullYear() + y, d.getMonth(), d.getDate());
  // 29 Şubat → artık olmayan yılda 1 Mart'a kayar; 28 Şubat'ta tut.
  if (x.getMonth() !== d.getMonth()) x.setDate(0);
  return x;
};
/** Kesirli yıl ekler: tam yıllar takvimle, kesir gün olarak (365,25). */
export const yilEkle = (d, y) => {
  const tam = Math.floor(y);
  return gunEkle(yilEkleTam(d, tam), Math.round((y - tam) * YIL_GUN));
};
export const gunFarki = (a, b) => Math.round((b.getTime() - a.getTime()) / MS_GUN);
export const yas = (dogum, tarih) => gunFarki(dogum, tarih) / YIL_GUN;
export const isoYaz = (d) => (d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '');
const enKucuk = (...t) => t.filter(Boolean).reduce((a, b) => (b < a ? b : a));
const enBuyuk = (...t) => t.filter(Boolean).reduce((a, b) => (b > a ? b : a));

/**
 * [bas, son] (iki uç dahil) aralığındaki ay sayısı: tam aylar 1, kalan gün/30.
 * 01.01–31.12 = 12; 05.10–31.12 = 2 ay 27 gün = 2,9.
 */
export function aySayisi(bas, son) {
  if (!bas || !son || son < bas) return 0;
  const bitis = gunEkle(son, 1);
  let ay = 0;
  let imlec = bas;
  for (;;) {
    const sonraki = new Date(bas.getFullYear(), bas.getMonth() + ay + 1, bas.getDate());
    // Ay sonu taşması (31 Ocak + 1 ay): ayın son gününe sabitle.
    if (sonraki.getDate() !== bas.getDate()) sonraki.setDate(0);
    if (sonraki > bitis) break;
    ay += 1;
    imlec = sonraki;
  }
  return ay + gunFarki(imlec, bitis) / 30;
}

/* ---------------- Yaşam tablosu ve evlenme ihtimali ---------------- */

/** Bakiye ömür sonu: verilen tarihteki yaşa göre TRH-2010. */
export function yasamSonu(dogum, tarih, cinsiyet) {
  const omur = getBakiyeOmur(yas(dogum, tarih), cinsiyet);
  return { omur, son: yilEkle(tarih, omur) };
}

/**
 * AYİM dul KADIN eşin yeniden evlenme ihtimali (%), hesap (rapor) tarihindeki
 * yaşa göre; 18 yaşından küçük her çocuk için 5 puan düşülür. Yargıtay
 * uygulaması (HGK 01.10.2019, 2017/2038 E. 2019/979 K.): 17–20 %52, 21–25 %40,
 * 26–30 %27, 31–35 %17, 36–40 %9, 41–50 %2, 51–55 %1. Erkek eş için Yargıtay'ın
 * benimsediği bir tablo yok; varsayılan olarak uygulanmaz.
 */
export const AYIM_EVLENME = [
  { ust: 20, oran: 52 }, { ust: 25, oran: 40 }, { ust: 30, oran: 27 }, { ust: 35, oran: 17 },
  { ust: 40, oran: 9 }, { ust: 50, oran: 2 }, { ust: 55, oran: 1 }, { ust: 200, oran: 0 },
];
export function evlenmeIhtimali(esYas, cocukSayisi18Alti = 0) {
  const r = AYIM_EVLENME.find((x) => (Number(esYas) || 0) <= x.ust) || AYIM_EVLENME[AYIM_EVLENME.length - 1];
  return Math.max(0, r.oran - 5 * Math.max(0, Number(cocukSayisi18Alti) || 0));
}

/* ---------------- Gelir ---------------- */

/**
 * Aylık net gelir fonksiyonu. Ücret girilmediyse asgari ücret; girildiyse
 * asgari ücretin artış oranında güncellenen ücret (asgari ücretin altına inmez).
 */
export function gelirFonksiyonu(olay, ucret, ilaveAgi = 0) {
  const u = sayi(ucret, 0);
  const olayAsgari = getAsgariNet(olay) || 1;
  // Eş/çocuk için ilave asgari geçim indirimi yalnız 2022 öncesinde vardı
  // (2022'den itibaren asgari ücret vergiden istisna, AGİ kalktı).
  const agi = Math.max(0, sayi(ilaveAgi, 0));
  const agiSonu = new Date(2022, 0, 1);
  return (t) => {
    const asg = getAsgariNet(t);
    const ek = agi > 0 && t < agiSonu ? agi : 0;
    if (!(u > 0)) return asg + ek;
    return Math.max(asg, u * (asg / olayAsgari)) + ek;
  };
}

/* ---------------- Dönem dilimleri ---------------- */

/**
 * [bas, son] aralığını verilen kırılma tarihlerinde böler. Kırılma tarihi
 * yeni dilimin İLK günüdür.
 */
function dilimle(bas, son, kirilmalar) {
  if (!bas || !son || son < bas) return [];
  const noktalar = [...new Set(kirilmalar
    .filter((k) => k && k > bas && k <= son)
    .map((k) => k.getTime()))].sort((a, b) => a - b).map((t) => new Date(t));
  const out = [];
  let s = bas;
  for (const k of noktalar) {
    out.push([s, gunEkle(k, -1)]);
    s = k;
  }
  out.push([s, son]);
  return out;
}

/** Asgari ücret değişim tarihleri ve yıl başları (geçmiş dönem kırılmaları). */
function gecmisKirilmalari(bas, son) {
  const k = [];
  for (let y = bas.getFullYear(); y <= son.getFullYear() + 1; y += 1) {
    k.push(new Date(y, 0, 1));
    k.push(new Date(y, 6, 1)); // yıl ortası asgari ücret artışları
  }
  return k;
}
function yilBaslari(bas, son) {
  const k = [];
  for (let y = bas.getFullYear() + 1; y <= son.getFullYear(); y += 1) k.push(new Date(y, 0, 1));
  return k;
}

/** Gelecek dönemde dilimin progresif rant çarpanı (k = hesap yılından sonraki yıl sırası, 1'den başlar). */
function rantCarpani(dilimBas, hesap, artis, iskonto) {
  const k = Math.max(1, dilimBas.getFullYear() - hesap.getFullYear() + 1);
  const g = artis / 100;
  const i = iskonto / 100;
  if (Math.abs(g - i) < 1e-12) return 1;
  return ((1 + g) / (1 + i)) ** k;
}

/* =====================================================================
   DÖNEM ÖZETİ (hesaplamadan önce gösterilen yıllar)
   ===================================================================== */

/**
 * Hesapla aynı kurallar: bakiye ömür olay tarihindeki yaşa göre, aktif dönem
 * 18–60 (çocukta 18 yaşından başlar), düzenli geliri yoksa bütün süre pasif.
 */
export function donemOzeti(input) {
  const olay = gunOku(input.olayTarihi);
  const hesap = gunOku(input.raporTarihi);
  const dogum = gunOku(input.dogumTarihi);
  if (!olay || !hesap || !dogum || hesap < olay) return null;
  const aktifBas = yilEkleTam(dogum, sayi(input.aktifBaslangicYas, 18));
  const aktifSon = input.tumuPasif ? gunEkle(dogum, -1) : gunEkle(yilEkleTam(dogum, sayi(input.aktifBitisYas, 60)), -1);
  const { omur, son: hayatSonu } = yasamSonu(dogum, olay, input.cinsiyet);
  const yil = (bas, son) => Math.max(0, gunFarki(bas, son) / YIL_GUN);
  const gelecekBas = gunEkle(hesap, 1);
  const gecmisGun = gunFarki(olay, hesap) + 1;
  return {
    olayYas: round2(yas(dogum, olay)),
    bakiyeOmur: round2(omur),
    faalCalisma: round2(yil(enBuyuk(olay, aktifBas), enKucuk(aktifSon, hayatSonu))),
    gecmisGun,
    gecmisYil: round2(gecmisGun / YIL_GUN),
    gelecekAktifYil: round2(yil(enBuyuk(gelecekBas, aktifBas), enKucuk(aktifSon, hayatSonu))),
    gelecekPasifYil: round2(yil(enBuyuk(gelecekBas, gunEkle(aktifSon, 1), aktifBas), hayatSonu)),
  };
}

/* =====================================================================
   İŞ GÜCÜ KAYBI
   ===================================================================== */

export function isGucuKaybiHesapla(input) {
  const p = {
    aktifBaslangicYas: 18, aktifBitisYas: 60, artisOrani: 10, iskontoOrani: 10,
    davaliKusur: 100, isGucuKayip: 0, sigortaOdemesi: 0, geciciGun: 0, geciciOran: 100,
    bakiciGun: 0, bakiciOran: 100, bakiciTur: 'gun', bakiciUcret: 'brut', bakiciAylik: null, pasifAylik: null, omurEsasi: 'olay', tumuPasif: false,
    ...input,
  };
  const olay = gunOku(p.olayTarihi);
  const hesap = gunOku(p.raporTarihi);
  const dogum = gunOku(p.dogumTarihi);
  if (!olay || !hesap || !dogum || hesap < olay) return null;

  const gelir = gelirFonksiyonu(olay, p.aylikUcret);
  // HGK 30.04.2025, 2024/4-643 E. 2025/264 K.: 2012'den önceki olaylarda PMF-1931 tablosu.
  const uyarilar = [];
  if (olay < new Date(2012, 0, 1)) uyarilar.push('Olay 2012 öncesi: Yargıtay HGK (30.04.2025, 2024/4-643 E. 2025/264 K.) bu olaylarda PMF-1931 yaşam tablosunu esas alıyor; bu hesap TRH-2010 kullanır, bakiye ömrü kontrol edin.');
  const kusur = Math.min(100, Math.max(0, sayi(p.davaliKusur, 100))) / 100;
  const kayip = Math.min(100, Math.max(0, sayi(p.isGucuKayip, 0))) / 100;
  const artis = sayi(p.artisOrani, 10);
  const iskonto = sayi(p.iskontoOrani, 10);

  const aktifBas = yilEkleTam(dogum, sayi(p.aktifBaslangicYas, 18));
  // Aktif dönem 18–60 (Yargıtay). Düzenli geliri olmayanda (ev hanımı, emekli) bütün süre pasif.
  const aktifSon = p.tumuPasif ? gunEkle(dogum, -1) : gunEkle(yilEkleTam(dogum, sayi(p.aktifBitisYas, 60)), -1);
  const omurTarihi = p.omurEsasi === 'rapor' ? hesap : olay;
  const { omur, son: hayatSonu } = yasamSonu(dogum, omurTarihi, p.cinsiyet);

  const satirlar = [];
  const ekle = (tur, bas, son, aylik, oran, carpan = 1) => {
    if (!bas || !son || son < bas) return 0;
    const ay = aySayisi(bas, son);
    const tutar = aylik * ay * oran * carpan;
    satirlar.push({ tur, bas: isoYaz(bas), son: isoYaz(son), ay: round2(ay), aylik: round2(aylik), oran: round2(oran * 100), carpan, tutar: round2(tutar) });
    return tutar;
  };

  // --- Geçici iş göremezlik: olaydan itibaren GİÖ günleri (%100 ya da girilen oran) ---
  const gGun = Math.max(0, Math.round(sayi(p.geciciGun, 0)));
  const gioSon = gGun > 0 ? gunEkle(olay, gGun - 1) : null;
  let gecici = 0;
  if (gGun > 0) {
    const bas = enBuyuk(olay, aktifBas);
    const son = enKucuk(gioSon, hesap, hayatSonu);
    for (const [s, e] of dilimle(bas, son, gecmisKirilmalari(bas, son))) {
      gecici += ekle('Geçici iş göremezlik', s, e, gelir(s), Math.min(100, sayi(p.geciciOran, 100)) / 100);
    }
  }

  // --- Bilinen dönem (sürekli iş göremezlik): GİÖ bitiminden hesap tarihine ---
  let gecmis = 0;
  {
    const bas = enBuyuk(gioSon ? gunEkle(gioSon, 1) : olay, aktifBas);
    const son = enKucuk(hesap, hayatSonu);
    if (bas <= son) {
      // Aktif yaşlar içinde gerçek gelir; aktif dönem bittiyse pasif gelir (asgari ücret).
      for (const [s, e] of dilimle(bas, son, [...gecmisKirilmalari(bas, son), gunEkle(aktifSon, 1)])) {
        const pasif = s > aktifSon;
        const aylik = pasif ? (sayi(p.pasifAylik, 0) > 0 ? sayi(p.pasifAylik) : getAsgariNetAgiHaric(s)) : gelir(s);
        gecmis += ekle(pasif ? 'Bilinen dönem (pasif)' : 'Bilinen dönem', s, e, aylik, kayip);
      }
    }
  }

  // --- Bilinmeyen dönem: hesap tarihinden sonra ---
  const gelecekBas = gunEkle(hesap, 1);
  const tabanAktif = gelir(hesap);
  const tabanPasif = sayi(p.pasifAylik, 0) > 0 ? sayi(p.pasifAylik) : getAsgariNetAgiHaric(hesap);
  let aktif = 0;
  let pasifTop = 0;
  {
    const bas = enBuyuk(gelecekBas, aktifBas);
    const son = enKucuk(aktifSon, hayatSonu);
    for (const [s, e] of dilimle(bas, son, yilBaslari(bas, son))) {
      aktif += ekle('Bilinmeyen aktif dönem', s, e, tabanAktif, kayip, rantCarpani(s, hesap, artis, iskonto));
    }
  }
  {
    const bas = enBuyuk(gelecekBas, gunEkle(aktifSon, 1), aktifBas);
    const son = hayatSonu;
    for (const [s, e] of dilimle(bas, son, yilBaslari(bas, son))) {
      pasifTop += ekle('Bilinmeyen pasif dönem', s, e, tabanPasif, kayip, rantCarpani(s, hesap, artis, iskonto));
    }
  }

  // --- Bakıcı gideri ---
  // Ücret esası: brüt ya da net asgari ücret (içtihat çelişkili; varsayılan brüt).
  // "gun": olaydan itibaren belirli gün (geçici bakıcı). "surekli": ömür boyu —
  // bilinen dönem dönemin asgari ücretiyle, gelecek dönem hesap tarihindekiyle
  // progresif rant. Oran: bakım ihtiyacı (tam bağımlı %100, kısmi %50 gibi).
  let bakici = 0;
  const bakimOrani = Math.min(100, Math.max(0, sayi(p.bakiciOran, 100))) / 100;
  const elleBakici = sayi(p.bakiciAylik, 0) > 0 ? sayi(p.bakiciAylik) : 0;
  const bakiciUcreti = (t) => elleBakici || (p.bakiciUcret === 'net' ? getAsgariNet(t) : getAsgariBrut(t));
  const bakiciEtiketi = `Bakıcı gideri (${p.bakiciUcret === 'net' ? 'net' : 'brüt'} asgari ücret)`;
  if (p.bakiciTur === 'surekli') {
    const son = enKucuk(hesap, hayatSonu);
    for (const [s, e] of dilimle(olay, son, gecmisKirilmalari(olay, son))) {
      bakici += ekle(bakiciEtiketi, s, e, bakiciUcreti(s), bakimOrani);
    }
    const bas = gunEkle(hesap, 1);
    for (const [s, e] of dilimle(bas, hayatSonu, yilBaslari(bas, hayatSonu))) {
      bakici += ekle(`${bakiciEtiketi} — gelecek`, s, e, bakiciUcreti(hesap), bakimOrani, rantCarpani(s, hesap, artis, iskonto));
    }
  } else {
    const bGun = Math.max(0, Math.round(sayi(p.bakiciGun, 0)));
    if (bGun > 0) {
      const son = enKucuk(gunEkle(olay, bGun - 1), hayatSonu);
      for (const [s, e] of dilimle(olay, son, gecmisKirilmalari(olay, son))) {
        bakici += ekle(bakiciEtiketi, s, e, bakiciUcreti(s), bakimOrani);
      }
    }
  }

  const brut = gecici + gecmis + aktif + pasifTop + bakici;
  const kusurlu = brut * kusur;
  const mahsup = Math.max(0, sayi(p.sigortaOdemesi, 0));

  return {
    type: 'isgucu',
    uyarilar,
    olayYas: round2(yas(dogum, olay)),
    hesapYas: round2(yas(dogum, hesap)),
    bakiyeOmur: round2(omur),
    hayatSonu: isoYaz(hayatSonu),
    aktifSon: isoYaz(aktifSon),
    gelirOlay: round2(gelir(olay)),
    gelirHesap: round2(tabanAktif),
    pasifAylik: round2(tabanPasif),
    geciciIsgoremezlik: round2(gecici * kusur),
    gecmis: round2(gecmis * kusur),
    gelecekAktif: round2(aktif * kusur),
    gelecekPasif: round2(pasifTop * kusur),
    bakiciGideri: round2(bakici * kusur),
    toplamBrut: round2(kusurlu),
    sigortaMahsup: round2(mahsup),
    toplam: round2(Math.max(0, kusurlu - mahsup)),
    kusurOrani: round2(kusur * 100),
    satirlar,
  };
}

/* =====================================================================
   DESTEKTEN YOKSUN KALMA
   ===================================================================== */

/**
 * hakSahipleri: [{ tip: 'es'|'cocuk'|'anne'|'baba', ad, dogumTarihi, cinsiyet,
 *                  yuksekOgrenim, psd }]
 */
export function destekHesapla(input) {
  const p = {
    aktifBaslangicYas: 18, aktifBitisYas: 60, artisOrani: 10, iskontoOrani: 10, davaliKusur: 100,
    mutevaffaPay: 2, esPay: 2, cocukPay: 1, ebeveynPay: 1, erkekSon: 18, kizSon: 22, uniSon: 25,
    pasifAylik: null, evlenmeUygula: true, erkekEsEvlenme: false, tumuPasif: false, hakSahipleri: [],
    ...input,
  };
  const olay = gunOku(p.olayTarihi);
  const hesap = gunOku(p.raporTarihi);
  const dogum = gunOku(p.dogumTarihi);
  if (!olay || !hesap || !dogum || hesap < olay) return null;

  const gelir = gelirFonksiyonu(olay, p.aylikUcret, p.ilaveAgi);
  // HGK 30.04.2025, 2024/4-643 E. 2025/264 K.: 2012'den önceki olaylarda PMF-1931 tablosu.
  const uyarilar = [];
  if (olay < new Date(2012, 0, 1)) uyarilar.push('Olay 2012 öncesi: Yargıtay HGK (30.04.2025, 2024/4-643 E. 2025/264 K.) bu olaylarda PMF-1931 yaşam tablosunu esas alıyor; bu hesap TRH-2010 kullanır, bakiye ömrü kontrol edin.');
  const kusur = Math.min(100, Math.max(0, sayi(p.davaliKusur, 100))) / 100;
  const artis = sayi(p.artisOrani, 10);
  const iskonto = sayi(p.iskontoOrani, 10);

  const aktifBas = yilEkleTam(dogum, sayi(p.aktifBaslangicYas, 18));
  const aktifSon = p.tumuPasif ? gunEkle(dogum, -1) : gunEkle(yilEkleTam(dogum, sayi(p.aktifBitisYas, 60)), -1);
  const { omur, son: hayatSonu } = yasamSonu(dogum, olay, p.cinsiyet);

  // Hak sahiplerinin destek bitişleri
  // Ölüm anında ana rahminde olan çocuk (cenin) doğduğu günden itibaren destek alır.
  const kisiler = (p.hakSahipleri || [])
    .map((h, i) => ({ ...h, dogum: gunOku(h.dogumTarihi), sira: i }))
    .filter((h) => h.dogum && (h.dogum <= olay || h.tip === 'cocuk'))
    .map((h) => {
      let bitis;
      if (h.tip === 'cocuk') {
        const sonYas = h.yuksekOgrenim ? sayi(p.uniSon, 25) : String(h.cinsiyet).toUpperCase() === 'K' ? sayi(p.kizSon, 22) : sayi(p.erkekSon, 18);
        bitis = gunEkle(yilEkleTam(h.dogum, sonYas), -1);
      } else {
        bitis = yasamSonu(h.dogum, olay, h.cinsiyet || (h.tip === 'baba' ? 'E' : 'K')).son;
      }
      const pay = h.tip === 'es' ? sayi(p.esPay, 2) : h.tip === 'cocuk' ? sayi(p.cocukPay, 1) : sayi(p.ebeveynPay, 1);
      return { ...h, baslangic: h.dogum > olay ? h.dogum : olay, bitis: enKucuk(bitis, hayatSonu), pay };
    });

  const sonTarih = hayatSonu;
  const kirilmalar = [
    ...gecmisKirilmalari(olay, hesap),
    gunEkle(hesap, 1),
    gunEkle(aktifSon, 1),
    ...yilBaslari(gunEkle(hesap, 1), sonTarih),
    ...kisiler.map((k) => gunEkle(k.bitis, 1)),
    ...kisiler.filter((k) => k.baslangic > olay).map((k) => k.baslangic),
  ];

  const sonuc = kisiler.map((k) => ({ ...k, gecmis: 0, gelecek: 0 }));
  const satirlar = [];
  const tabanAktif = gelir(hesap);
  const tabanPasif = sayi(p.pasifAylik, 0) > 0 ? sayi(p.pasifAylik) : getAsgariNetAgiHaric(hesap);

  for (const [s, e] of dilimle(olay, sonTarih, kirilmalar)) {
    const gelecekMi = s > hesap;
    const pasifMi = s > aktifSon;
    const ay = aySayisi(s, e);
    if (ay <= 0) continue;

    // Gelir: geçmişte dönemin geliri; gelecekte hesap tarihindeki gelir × rant çarpanı.
    // 18 yaşından önce müteveffanın geliri yok (destek de yok).
    let aylik = 0;
    if (s >= aktifBas) {
      if (!gelecekMi) aylik = pasifMi ? (sayi(p.pasifAylik, 0) > 0 ? sayi(p.pasifAylik) : getAsgariNetAgiHaric(s)) : gelir(s);
      else aylik = (pasifMi ? tabanPasif : tabanAktif) * rantCarpani(s, hesap, artis, iskonto);
    }
    const donemGeliri = aylik * ay;

    const hakli = sonuc.filter((k) => s >= k.baslangic && s <= k.bitis);
    const toplamPay = sayi(p.mutevaffaPay, 2) + hakli.reduce((t, k) => t + k.pay, 0);
    const satir = { bas: isoYaz(s), son: isoYaz(e), ay: round2(ay), aylik: round2(aylik), donem: gelecekMi ? 'gelecek' : 'gecmis', pasif: pasifMi, paylar: {} };
    if (hakli.length && toplamPay > 0 && donemGeliri > 0) {
      for (const k of hakli) {
        const tutar = donemGeliri * (k.pay / toplamPay);
        if (gelecekMi) k.gelecek += tutar; else k.gecmis += tutar;
        satir.paylar[k.sira] = { pay: `${k.pay}/${toplamPay}`, tutar: round2(tutar) };
      }
    }
    satirlar.push(satir);
  }

  // Eşin yeniden evlenme ihtimali: hesap tarihindeki yaşına ve 18 yaş altı çocuk sayısına göre, gelecek paydan.
  const cocuk18 = kisiler.filter((k) => k.tip === 'cocuk' && yas(k.dogum, hesap) < 18).length;
  const dagilim = sonuc.map((k) => {
    let evlenme = 0;
    const kadinEs = String(k.cinsiyet || 'K').toUpperCase() === 'K';
    if (k.tip === 'es' && p.evlenmeUygula && !kadinEs && !p.erkekEsEvlenme) {
      uyarilar.push('Erkek eş için Yargıtay’ın benimsediği bir yeniden evlenme tablosu yok; evlenme indirimi uygulanmadı.');
    }
    if (k.tip === 'es' && p.evlenmeUygula && (kadinEs || p.erkekEsEvlenme)) {
      evlenme = evlenmeIhtimali(yas(k.dogum, hesap), cocuk18);
    }
    const gelecekNet = k.gelecek * (1 - evlenme / 100);
    const brut = (k.gecmis + gelecekNet) * kusur;
    const psd = Math.max(0, sayi(k.psd, 0));
    return {
      sira: k.sira,
      tip: k.tip,
      ad: k.ad || { es: 'Eş', anne: 'Anne', baba: 'Baba', cocuk: 'Çocuk' }[k.tip],
      pay: k.pay,
      destekBitis: isoYaz(k.bitis),
      gecmis: round2(k.gecmis * kusur),
      gelecek: round2(gelecekNet * kusur),
      evlenmeIhtimali: evlenme,
      evlenmeIndirimi: round2((k.gelecek - gelecekNet) * kusur),
      sgkPsd: round2(psd),
      toplam: round2(Math.max(0, brut - psd)),
    };
  });

  return {
    type: 'destek',
    uyarilar,
    olayYas: round2(yas(dogum, olay)),
    bakiyeOmur: round2(omur),
    hayatSonu: isoYaz(hayatSonu),
    aktifSon: isoYaz(aktifSon),
    gelirOlay: round2(gelir(olay)),
    gelirHesap: round2(tabanAktif),
    pasifAylik: round2(tabanPasif),
    kusurOrani: round2(kusur * 100),
    dagilim,
    gecmis: round2(dagilim.reduce((t, d) => t + d.gecmis, 0)),
    gelecek: round2(dagilim.reduce((t, d) => t + d.gelecek, 0)),
    genelToplam: round2(dagilim.reduce((t, d) => t + d.toplam, 0)),
    satirlar,
  };
}
